import * as React from 'react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddBusinessRoundedIcon from '@mui/icons-material/AddBusinessRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded';
import AdminLayout, { AdminSection } from '../../components/AdminLayout';
import axiosInstance from '../../utils/axiosInstance';
import { getBusinessRoleLabel } from '../../utils/userProfile';

const emptyUserForm = { email: '', full_name: '', role: 'partner_user' };
const emptyUserEditForm = { status: 'active', role: 'partner_user' };
const emptyMoveForm = { target_partner_id: '', role: 'partner_user' };
const emptyAddress = {
  line1: '',
  line2: '',
  city: '',
  region: '',
  postal_code: '',
  country: 'US',
};
const emptyBusinessUnitForm = {
  business_unit_code: '',
  display_name: '',
  legal_name: '',
  status: 'active',
  contact_email: '',
  contact_phone: '',
  external_sharing_enabled: false,
  same_as_parent_address: true,
  physical_main_address: '',
  physical_address: { ...emptyAddress },
};
const emptyGatewayForm = { gateway_code: '', display_name: '', status: 'active' };
const emptyCapabilityForm = {
  business_unit_id: '',
  category_id: '',
  source_capability_definition_id: '',
  capability_name: '',
  global_response: 'true',
  properties: '{\n  "test_mode": true\n}',
};

const userStatusOptions = [
  { value: 'active', label: 'Active' },
  { value: 'pending_login', label: 'Pending Login' },
  { value: 'invited', label: 'Invited' },
  { value: 'inactive', label: 'Inactive' },
  { value: 'suspended', label: 'Suspended' },
];

const userRoleOptions = [
  { value: 'partner_user', label: 'User' },
  { value: 'partner_admin', label: 'Admin' },
];

const partnerDetailTabs = [
  { value: 'profile', label: 'Profile' },
  { value: 'business-units', label: 'Business Units' },
  { value: 'users', label: 'Users' },
  { value: 'need-categories', label: 'Need Categories' },
  { value: 'gateways', label: 'Gateways' },
  { value: 'capabilities', label: 'Capabilities & Assets' },
];

function getUserLabel(user) {
  return user?.full_name || user?.email || user?.cognito_sub || 'Unnamed user';
}

function getUserInitials(user) {
  const source = getUserLabel(user);
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
}

function getStatusChipColor(status) {
  if (status === 'active') {
    return 'success';
  }
  if (status === 'invited' || status === 'pending_login') {
    return 'info';
  }
  if (status === 'suspended') {
    return 'warning';
  }
  return 'default';
}

function getInviteStatusColor(status) {
  if (status === 'pending') {
    return 'info';
  }
  if (status === 'expired') {
    return 'warning';
  }
  return 'default';
}

function getInviteLabel(invite) {
  return invite?.full_name || invite?.email || invite?.invite_id || 'Pending invite';
}

function formatDate(value) {
  if (!value) {
    return 'Never';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleString();
}

function getPartnerLabel(partner) {
  return partner?.display_name
    || partner?.business_unit_code
    || partner?.partner_code
    || partner?.business_unit_id
    || partner?.partner_id
    || 'Unknown target';
}

function getTargetId(target) {
  return target?.business_unit_id || target?.partner_id || '';
}

function isBusinessUnit(target) {
  return target?.entity_type === 'business_unit' || Boolean(target?.business_unit_id);
}

function getPartnerSecondary(partner) {
  return [
    partner?.business_unit_code || partner?.partner_code,
    isBusinessUnit(partner) ? 'Business Unit' : 'Partner',
    partner?.status,
  ].filter(Boolean).join(' - ');
}

function getPartnerOptionLabel(partner) {
  const label = getPartnerLabel(partner);
  const targetCode = partner?.business_unit_code || partner?.partner_code;
  const code = targetCode ? ` (${targetCode})` : '';
  const type = isBusinessUnit(partner) ? ' - Business Unit' : ' - Partner';
  return `${label}${code}${type}`;
}

function getBusinessUnitId(unit) {
  return unit?.business_unit_id || '';
}

function getBusinessUnitLabel(unit) {
  return unit?.business_unit_display_name
    || unit?.business_unit_code
    || unit?.display_name
    || unit?.business_unit_id
    || 'Business Unit';
}

function getBusinessUnitOptionsFromData(data) {
  const units = Array.isArray(data?.business_units) ? data.business_units : [];
  if (units.length) {
    return units;
  }
  return [];
}

function getDefaultBusinessUnitId(data) {
  const options = getBusinessUnitOptionsFromData(data);
  const defaultUnit = options.find((unit) => unit?.metadata?.is_default_business_unit);
  return getBusinessUnitId(defaultUnit || options[0]);
}

function cloneAddress(address = {}) {
  return {
    ...emptyAddress,
    ...(address && typeof address === 'object' ? address : {}),
    region: address?.region || address?.state || '',
    postal_code: address?.postal_code || address?.zipcode || address?.zip || '',
    country: address?.country || 'US',
  };
}

function formatAddress(address) {
  return [
    address?.line1,
    address?.line2,
    address?.city,
    address?.region,
    address?.postal_code,
    address?.country,
  ].map((value) => String(value || '').trim()).filter(Boolean).join(', ');
}

function addressPayload(address, fallbackText = '') {
  const payload = cloneAddress(address);
  const formatted = formatAddress(payload) || fallbackText;
  if (formatted) {
    payload.formatted = formatted;
  }
  return payload;
}

function businessUnitFormFromUnit(unit) {
  const metadata = unit?.metadata && typeof unit.metadata === 'object' ? unit.metadata : {};
  const physicalAddress = addressPayload(unit?.physical_address, unit?.physical_main_address || '');
  return {
    business_unit_code: unit?.business_unit_code || '',
    display_name: unit?.display_name || '',
    legal_name: unit?.legal_name || '',
    status: unit?.status || 'active',
    contact_email: unit?.contact_email || '',
    contact_phone: unit?.contact_phone || '',
    external_sharing_enabled: Boolean(unit?.external_sharing_enabled),
    same_as_parent_address: Boolean(metadata.same_as_parent_address),
    physical_main_address: unit?.physical_main_address || formatAddress(physicalAddress),
    physical_address: physicalAddress,
  };
}

function freshBusinessUnitForm() {
  return {
    ...emptyBusinessUnitForm,
    physical_address: { ...emptyAddress },
  };
}

const assetRelationshipOptions = [
  'operational_asset',
  'facility_sensor',
  'in_situ_sensor',
  'environment_sensor',
  'standalone',
];

const emptyAssetForm = {
  business_unit_id: '',
  source_capability_asset_id: '',
  asset_name: '',
  asset_type: '',
  asset_relationship_type: 'operational_asset',
  parent_asset_id: '',
  facility_id: '',
  location_label: '',
  location_metadata: '{}',
  global_response: 'true',
  latitude: '',
  longitude: '',
  properties: '{}',
};

function sanitizeFilenamePart(value, fallback = 'gateway') {
  return String(value || '')
    .trim()
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/^-+|-+$/g, '') || fallback;
}

function filenameFromContentDisposition(value) {
  if (!value) {
    return '';
  }

  const utf8Match = value.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, '');
    }
  }

  const asciiMatch = value.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1]?.trim() || '';
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function triggerUrlDownload(url, filename) {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
}

async function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    const text = await data.text();
    if (!text) {
      return fallback;
    }
    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || fallback;
    } catch {
      return text;
    }
  }
  return data?.message || error?.message || fallback;
}

function capabilityToForm(capability) {
  return {
    business_unit_id: capability?.business_unit_id || '',
    category_id: capability?.category_id || '',
    source_capability_definition_id: capability?.source_capability_definition_id || '',
    capability_name: capability?.capability_name || '',
    global_response: capability?.global_response ? 'true' : 'false',
    properties: JSON.stringify(capability?.properties || {}, null, 2),
  };
}

function assetToForm(asset) {
  const relationshipType = assetRelationshipOptions.includes(asset?.asset_relationship_type)
    ? asset.asset_relationship_type
    : 'operational_asset';
  return {
    business_unit_id: asset?.business_unit_id || '',
    source_capability_asset_id: asset?.source_capability_asset_id || '',
    asset_name: asset?.asset_name || '',
    asset_type: asset?.asset_type || '',
    asset_relationship_type: relationshipType,
    parent_asset_id: asset?.parent_asset_id || '',
    facility_id: asset?.facility_id || '',
    location_label: asset?.location_label || '',
    location_metadata: JSON.stringify(asset?.location_metadata || {}, null, 2),
    global_response: asset?.global_response ? 'true' : 'false',
    latitude: asset?.latitude ?? '',
    longitude: asset?.longitude ?? '',
    properties: JSON.stringify(asset?.properties || {}, null, 2),
  };
}

export default function AdminPartnerDetailPage(props) {
  const { partner_id: partnerId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: '', data: null });
  const [activeTab, setActiveTab] = useState('profile');
  const [partnerForm, setPartnerForm] = useState({});
  const [userForm, setUserForm] = useState(emptyUserForm);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [editingInvite, setEditingInvite] = useState(null);
  const [inviteEditForm, setInviteEditForm] = useState({
    target_partner_id: '',
    full_name: '',
    role: 'partner_user',
  });
  const [savingInviteEdit, setSavingInviteEdit] = useState(false);
  const [resendingInviteId, setResendingInviteId] = useState('');
  const [revokingInvite, setRevokingInvite] = useState(null);
  const [savingInviteRevoke, setSavingInviteRevoke] = useState(false);
  const [selectedUserTargetId, setSelectedUserTargetId] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [userEditForm, setUserEditForm] = useState(emptyUserEditForm);
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  const [movingUser, setMovingUser] = useState(null);
  const [moveForm, setMoveForm] = useState(emptyMoveForm);
  const [movingUserSaving, setMovingUserSaving] = useState(false);
  const [removingUser, setRemovingUser] = useState(null);
  const [removingUserSaving, setRemovingUserSaving] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [deletingUserSaving, setDeletingUserSaving] = useState(false);
  const [deletingPartner, setDeletingPartner] = useState(false);
  const [deletingPartnerSaving, setDeletingPartnerSaving] = useState(false);
  const [businessUnitForm, setBusinessUnitForm] = useState(freshBusinessUnitForm);
  const [editingBusinessUnitId, setEditingBusinessUnitId] = useState('');
  const [savingBusinessUnit, setSavingBusinessUnit] = useState(false);
  const [deletingBusinessUnit, setDeletingBusinessUnit] = useState(null);
  const [deletingBusinessUnitSaving, setDeletingBusinessUnitSaving] = useState(false);
  const [partnerOptions, setPartnerOptions] = useState([]);
  const [partnerOptionsLoading, setPartnerOptionsLoading] = useState(false);
  const [partnerOptionsError, setPartnerOptionsError] = useState('');
  const [gatewayForm, setGatewayForm] = useState(emptyGatewayForm);
  const [capabilityForm, setCapabilityForm] = useState(emptyCapabilityForm);
  const [assetForm, setAssetForm] = useState(emptyAssetForm);
  const [needCategories, setNeedCategories] = useState([]);
  const [partnerNeedCategoryIds, setPartnerNeedCategoryIds] = useState([]);
  const [needCategoryDraft, setNeedCategoryDraft] = useState(null);
  const [creatingCapability, setCreatingCapability] = useState(false);
  const [creatingAsset, setCreatingAsset] = useState(false);
  const [savingNeedCategories, setSavingNeedCategories] = useState(false);
  const [editingCapability, setEditingCapability] = useState(null);
  const [capabilityEditForm, setCapabilityEditForm] = useState(emptyCapabilityForm);
  const [savingCapabilityEdit, setSavingCapabilityEdit] = useState(false);
  const [deletingCapability, setDeletingCapability] = useState(null);
  const [deletingCapabilitySaving, setDeletingCapabilitySaving] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [assetEditForm, setAssetEditForm] = useState(emptyAssetForm);
  const [savingAssetEdit, setSavingAssetEdit] = useState(false);
  const [associatingCapabilityId, setAssociatingCapabilityId] = useState('');
  const [associateAssetId, setAssociateAssetId] = useState('');
  const [associatingAssetSaving, setAssociatingAssetSaving] = useState(false);
  const [deletingAssetContext, setDeletingAssetContext] = useState(null);
  const [deletingAssetSaving, setDeletingAssetSaving] = useState(false);
  const [removingNeedCategoryId, setRemovingNeedCategoryId] = useState('');
  const [creatingGateway, setCreatingGateway] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  async function loadPartner() {
    setState((current) => ({ ...current, loading: true, error: '' }));
    try {
      const response = await axiosInstance.get(`/admin/partners/${partnerId}`);
      setState({ loading: false, error: '', data: response.data });
      setPartnerForm(response.data?.partner || {});
      const defaultBusinessUnitId = getDefaultBusinessUnitId(response.data);
      const nextTargets = Array.isArray(response.data?.user_targets)
        ? response.data.user_targets
        : response.data?.partner
          ? [response.data.partner]
          : [];
      const routeTargetId = response.data?.partner?.partner_id || partnerId;
      const routeTarget = nextTargets.find((target) => getTargetId(target) === routeTargetId);
      setSelectedUserTargetId((current) => (
        nextTargets.some((target) => getTargetId(target) === current)
          ? current
          : getTargetId(routeTarget) || getTargetId(nextTargets[0])
      ));
      setPartnerNeedCategoryIds(
        Array.isArray(response.data?.partner?.need_categories)
          ? response.data.partner.need_categories
          : [],
      );
      setCapabilityForm((current) => ({
        ...current,
        business_unit_id: current.business_unit_id || defaultBusinessUnitId,
        category_id:
          current.category_id ||
          response.data?.partner?.need_categories?.[0] ||
          '',
      }));
      const firstAssetType = Array.isArray(response.data?.asset_types)
        ? response.data.asset_types.find((assetType) => assetType.status === 'active')
        : null;
      setAssetForm((current) => ({
        ...current,
        business_unit_id: current.business_unit_id || defaultBusinessUnitId,
        asset_type: current.asset_type || firstAssetType?.asset_type_code || '',
      }));
    } catch (error) {
      setState({
        loading: false,
        error: error?.response?.data?.message || 'Unable to load partner details.',
        data: null,
      });
    }
  }

  useEffect(() => {
    loadPartner();
  }, [partnerId]);

  useEffect(() => {
    async function loadNeedCategories() {
      try {
        const response = await axiosInstance.get('/admin/need-categories');
        const categories = Array.isArray(response.data?.need_categories)
          ? response.data.need_categories
          : [];
        setNeedCategories(categories.filter((category) => category?.status !== 'retired'));
      } catch (error) {
        setNeedCategories([]);
      }
    }

    loadNeedCategories();
  }, []);

  useEffect(() => {
    let isActive = true;

    async function loadPartnerOptions() {
      setPartnerOptionsLoading(true);
      setPartnerOptionsError('');
      try {
        const response = await axiosInstance.get('/admin/partners');
        if (!isActive) {
          return;
        }
        const options = Array.isArray(response.data?.partners) ? response.data.partners : [];
        setPartnerOptions(options);
      } catch (error) {
        if (!isActive) {
          return;
        }
        setPartnerOptions([]);
        setPartnerOptionsError(error?.response?.data?.message || 'Unable to load partner move targets.');
      } finally {
        if (isActive) {
          setPartnerOptionsLoading(false);
        }
      }
    }

    loadPartnerOptions();

    return () => {
      isActive = false;
    };
  }, []);

  async function handlePartnerSave(event) {
    event.preventDefault();
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.patch(`/admin/partners/${partnerId}`, {
        display_name: partnerForm.display_name,
        legal_name: partnerForm.legal_name,
        status: partnerForm.status,
        contact_email: partnerForm.contact_email,
        contact_phone: partnerForm.contact_phone,
        external_sharing_enabled: Boolean(partnerForm.external_sharing_enabled),
        child_external_sharing_enabled: Boolean(partnerForm.child_external_sharing_enabled),
      });
      setFeedback('Partner details updated.');
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to update partner.');
    }
  }

  function getUserTargetsFromState() {
    if (Array.isArray(state.data?.user_targets) && state.data.user_targets.length) {
      return state.data.user_targets;
    }
    return state.data?.partner ? [state.data.partner] : [];
  }

  function getCurrentUserTarget() {
    const targets = getUserTargetsFromState();
    return targets.find((target) => getTargetId(target) === selectedUserTargetId) || targets[0] || state.data?.partner || null;
  }

  function getCurrentUserTargetId() {
    return getTargetId(getCurrentUserTarget()) || partnerId;
  }

  function getUserSourcePartnerId(user) {
    return user?.partner_id || getCurrentUserTargetId();
  }

  function updateBusinessUnitForm(field, value) {
    setBusinessUnitForm((current) => ({ ...current, [field]: value }));
  }

  function updateBusinessUnitAddress(field, value) {
    setBusinessUnitForm((current) => {
      const physicalAddress = { ...current.physical_address, [field]: value };
      return {
        ...current,
        physical_address: physicalAddress,
        physical_main_address: formatAddress(physicalAddress),
      };
    });
  }

  function resetBusinessUnitForm() {
    setBusinessUnitForm(freshBusinessUnitForm());
    setEditingBusinessUnitId('');
  }

  function openBusinessUnitEditor(unit) {
    setBusinessUnitForm(businessUnitFormFromUnit(unit));
    setEditingBusinessUnitId(unit.business_unit_id);
    setActiveTab('business-units');
    setFeedback('');
    setErrorMessage('');
  }

  function buildBusinessUnitPayload() {
    const parentAddress = addressPayload(partner?.physical_address, partner?.physical_main_address || '');
    const physicalAddress = businessUnitForm.same_as_parent_address
      ? parentAddress
      : addressPayload(businessUnitForm.physical_address, businessUnitForm.physical_main_address);
    return {
      business_unit_code: businessUnitForm.business_unit_code,
      display_name: businessUnitForm.display_name,
      legal_name: businessUnitForm.legal_name || null,
      status: businessUnitForm.status,
      contact_email: businessUnitForm.contact_email || null,
      contact_phone: businessUnitForm.contact_phone || null,
      external_sharing_enabled: Boolean(businessUnitForm.external_sharing_enabled),
      physical_main_address: businessUnitForm.same_as_parent_address
        ? parentAddress.formatted || formatAddress(parentAddress)
        : formatAddress(physicalAddress),
      physical_address: physicalAddress,
      metadata: {
        same_as_parent_address: Boolean(businessUnitForm.same_as_parent_address),
      },
    };
  }

  async function handleSaveBusinessUnit(event) {
    event.preventDefault();
    if (!owningPartnerId) {
      setErrorMessage('A partner is required before managing business units.');
      return;
    }

    setSavingBusinessUnit(true);
    setFeedback('');
    setErrorMessage('');
    try {
      const payload = buildBusinessUnitPayload();
      if (editingBusinessUnitId) {
        await axiosInstance.patch(
          `/admin/partners/${owningPartnerId}/business-units/${editingBusinessUnitId}`,
          payload,
        );
        setFeedback('Business unit updated.');
      } else {
        await axiosInstance.post(`/admin/partners/${owningPartnerId}/business-units`, payload);
        setFeedback('Business unit created.');
      }
      resetBusinessUnitForm();
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to save business unit.');
    } finally {
      setSavingBusinessUnit(false);
    }
  }

  function openDeleteBusinessUnitDialog(unit) {
    setDeletingBusinessUnit(unit);
    setFeedback('');
    setErrorMessage('');
  }

  function closeDeleteBusinessUnitDialog() {
    if (!deletingBusinessUnitSaving) {
      setDeletingBusinessUnit(null);
    }
  }

  async function handleDeleteBusinessUnit() {
    if (!deletingBusinessUnit || !owningPartnerId) {
      return;
    }

    const label = getPartnerLabel(deletingBusinessUnit);
    setDeletingBusinessUnitSaving(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.delete(
        `/admin/partners/${owningPartnerId}/business-units/${deletingBusinessUnit.business_unit_id}`,
      );
      setFeedback(`Deleted ${label}.`);
      setDeletingBusinessUnit(null);
      if (selectedUserTargetId === deletingBusinessUnit.business_unit_id) {
        setSelectedUserTargetId(owningPartnerId);
      }
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to delete business unit.');
    } finally {
      setDeletingBusinessUnitSaving(false);
    }
  }

  async function handleAddUser(event) {
    event.preventDefault();
    setFeedback('');
    setErrorMessage('');
    setInviteSaving(true);
    try {
      const target = getCurrentUserTarget();
      if (isBusinessUnit(target)) {
        await axiosInstance.post(
          `/admin/partners/${partnerId}/business-units/${target.business_unit_id}/users`,
          userForm,
        );
      } else {
        await axiosInstance.post(`/admin/partners/${partnerId}/users`, userForm);
      }
      setUserForm(emptyUserForm);
      setInviteOpen(false);
      setFeedback(`User invited to ${getPartnerLabel(target)}.`);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to invite partner user.');
    } finally {
      setInviteSaving(false);
    }
  }

  async function handleUpdateUser(user, updates) {
    setFeedback('');
    setErrorMessage('');
    try {
      const target = getCurrentUserTarget();
      if (isBusinessUnit(target)) {
        await axiosInstance.patch(
          `/admin/partners/${partnerId}/business-units/${target.business_unit_id}/users/${user.cognito_sub}`,
          { ...updates, role: 'partner_user' },
        );
      } else {
        await axiosInstance.patch(`/admin/partners/${partnerId}/users/${user.cognito_sub}`, updates);
      }
      setFeedback(`Updated ${user.email || user.full_name || user.cognito_sub}.`);
      await loadPartner();
      return true;
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to update partner user.');
      return false;
    }
  }

  function openInviteDialog() {
    setUserForm(emptyUserForm);
    setInviteOpen(true);
    setFeedback('');
    setErrorMessage('');
  }

  function closeInviteDialog() {
    if (!inviteSaving) {
      setInviteOpen(false);
    }
  }

  function openInviteEditor(invite) {
    setEditingInvite(invite);
    setInviteEditForm({
      target_partner_id: invite?.partner_id || getCurrentUserTargetId(),
      full_name: invite?.full_name || '',
      role: invite?.role || 'partner_user',
    });
    setFeedback('');
    setErrorMessage('');
  }

  function closeInviteEditor() {
    if (!savingInviteEdit) {
      setEditingInvite(null);
    }
  }

  async function handleSaveInviteEdit(event) {
    event.preventDefault();
    if (!editingInvite) {
      return;
    }
    if (!inviteEditForm.target_partner_id) {
      setErrorMessage('Select a target partner before saving the invite.');
      return;
    }

    setSavingInviteEdit(true);
    setFeedback('');
    setErrorMessage('');
    try {
      const response = await axiosInstance.patch(`/admin/invites/${editingInvite.invite_id}`, {
        target_partner_id: inviteEditForm.target_partner_id,
        full_name: inviteEditForm.full_name || null,
        role: inviteEditForm.role,
      });
      setFeedback(`Updated invite for ${getInviteLabel(response.data?.invite || editingInvite)}.`);
      setEditingInvite(null);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to update invite.');
    } finally {
      setSavingInviteEdit(false);
    }
  }

  async function resendInvite(invite) {
    if (!invite?.invite_id) {
      return;
    }
    setResendingInviteId(invite.invite_id);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.post(`/admin/invites/${invite.invite_id}/resend`);
      setFeedback(`Resent invite to ${invite.email}.`);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to resend invite.');
    } finally {
      setResendingInviteId('');
    }
  }

  function openRevokeInviteDialog(invite) {
    setRevokingInvite(invite);
    setFeedback('');
    setErrorMessage('');
  }

  function closeRevokeInviteDialog() {
    if (!savingInviteRevoke) {
      setRevokingInvite(null);
    }
  }

  async function revokeInvite() {
    if (!revokingInvite?.invite_id) {
      return;
    }
    const label = getInviteLabel(revokingInvite);
    setSavingInviteRevoke(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.delete(`/admin/invites/${revokingInvite.invite_id}`);
      setFeedback(`Revoked invite for ${label}.`);
      setRevokingInvite(null);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to revoke invite.');
    } finally {
      setSavingInviteRevoke(false);
    }
  }

  function openUserEditor(user) {
    setEditingUser(user);
    setUserEditForm({
      status: user?.status || 'active',
      role: user?.role || 'partner_user',
    });
    setFeedback('');
    setErrorMessage('');
  }

  function closeUserEditor() {
    if (!savingUserEdit) {
      setEditingUser(null);
      setUserEditForm(emptyUserEditForm);
    }
  }

  async function handleSaveUserEdit(event) {
    event.preventDefault();
    if (!editingUser) {
      return;
    }

    setSavingUserEdit(true);
    try {
      const updated = await handleUpdateUser(editingUser, userEditForm);
      if (updated) {
        setEditingUser(null);
        setUserEditForm(emptyUserEditForm);
      }
    } finally {
      setSavingUserEdit(false);
    }
  }

  function openMoveUserDialog(user) {
    setMovingUser(user);
    setMoveForm({
      target_partner_id: '',
      role: user?.role || 'partner_user',
    });
    setFeedback('');
    setErrorMessage('');
  }

  function closeMoveUserDialog() {
    if (!movingUserSaving) {
      setMovingUser(null);
      setMoveForm(emptyMoveForm);
    }
  }

  async function handleMoveUser(event) {
    event.preventDefault();
    if (!movingUser) {
      return;
    }
    if (!moveForm.target_partner_id) {
      setErrorMessage('Select a target partner before moving the user.');
      return;
    }
    if (isBusinessUnit(getCurrentUserTarget())) {
      setErrorMessage('Business-unit access is managed independently and cannot be moved as a partner membership.');
      return;
    }

    setFeedback('');
    setErrorMessage('');
    setMovingUserSaving(true);
    try {
      const response = await axiosInstance.post(
        `/admin/partners/${getUserSourcePartnerId(movingUser)}/users/${movingUser.cognito_sub}/move`,
        {
          target_partner_id: moveForm.target_partner_id,
          role: moveForm.role,
        },
      );
      const targetLabel = getPartnerLabel(response.data?.target_partner);
      setFeedback(`Moved ${getUserLabel(movingUser)} to ${targetLabel}.`);
      setMovingUser(null);
      setMoveForm(emptyMoveForm);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to move partner user.');
    } finally {
      setMovingUserSaving(false);
    }
  }

  function openRemoveUserDialog(user) {
    setRemovingUser(user);
    setFeedback('');
    setErrorMessage('');
  }

  function closeRemoveUserDialog() {
    if (!removingUserSaving) {
      setRemovingUser(null);
    }
  }

  async function handleDeleteUser() {
    if (!removingUser) {
      return;
    }

    const label = getUserLabel(removingUser);
    setFeedback('');
    setErrorMessage('');
    setRemovingUserSaving(true);
    try {
      const target = getCurrentUserTarget();
      if (isBusinessUnit(target)) {
        await axiosInstance.delete(
          `/admin/partners/${partnerId}/business-units/${target.business_unit_id}/users/${removingUser.cognito_sub}`,
        );
      } else {
        await axiosInstance.delete(`/admin/partners/${partnerId}/users/${removingUser.cognito_sub}`);
      }
      setFeedback(`Removed ${label} from ${getPartnerLabel(getCurrentUserTarget())}.`);
      setRemovingUser(null);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to remove partner user.');
    } finally {
      setRemovingUserSaving(false);
    }
  }

  function openDeleteUserDialog(user) {
    setDeletingUser(user);
    setFeedback('');
    setErrorMessage('');
  }

  function closeDeleteUserDialog() {
    if (!deletingUserSaving) {
      setDeletingUser(null);
    }
  }

  async function handleDeleteUserCompletely() {
    if (!deletingUser) {
      return;
    }

    const label = getUserLabel(deletingUser);
    setFeedback('');
    setErrorMessage('');
    setDeletingUserSaving(true);
    try {
      await axiosInstance.delete(`/admin/users/${deletingUser.cognito_sub}`);
      setFeedback(`Deleted ${label} from AMX.`);
      setDeletingUser(null);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to delete user from AMX.');
    } finally {
      setDeletingUserSaving(false);
    }
  }

  function openDeletePartnerDialog() {
    setDeletingPartner(true);
    setFeedback('');
    setErrorMessage('');
  }

  function closeDeletePartnerDialog() {
    if (!deletingPartnerSaving) {
      setDeletingPartner(false);
    }
  }

  async function handleDeletePartnerAccount() {
    if (!partner) {
      return;
    }

    setFeedback('');
    setErrorMessage('');
    setDeletingPartnerSaving(true);
    try {
      await axiosInstance.delete(`/admin/partners/${partnerId}`);
      navigate('/admin/partners', {
        replace: true,
        state: { feedback: `${getPartnerLabel(partner)} deleted.` },
      });
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to delete partner.');
    } finally {
      setDeletingPartnerSaving(false);
    }
  }

  async function handleCreateGateway(event) {
    event.preventDefault();
    setFeedback('');
    setErrorMessage('');
    setCreatingGateway(true);
    try {
      const response = await axiosInstance.post(`/admin/partners/${partnerId}/gateways`, {
        gateway_code: gatewayForm.gateway_code,
        display_name: gatewayForm.display_name,
        status: gatewayForm.status,
        iot: { mode: 'create_new' },
      }, {
        responseType: 'blob',
      });

      const gatewayCode = response.headers['x-amx-gateway-code'] || gatewayForm.gateway_code;
      const safeGatewayCode = sanitizeFilenamePart(gatewayCode);
      const bootstrapFilename =
        filenameFromContentDisposition(response.headers['content-disposition']) ||
        `${safeGatewayCode}-bootstrap.zip`;
      const packageUrl = response.headers['x-amx-deployment-package-url'];
      const packageFilename =
        response.headers['x-amx-deployment-package-filename'] ||
        `${safeGatewayCode}-deployment_package.zip`;
      const responseContentType = response.headers['content-type'] || response.data?.type || '';

      const downloadedBootstrap =
        response.data instanceof Blob &&
        response.data.size > 0 &&
        responseContentType.toLowerCase().includes('application/zip');
      if (packageUrl) {
        triggerUrlDownload(packageUrl, packageFilename);
      }
      if (downloadedBootstrap) {
        triggerBlobDownload(response.data, bootstrapFilename);
      }

      let gatewayFeedback = 'Gateway deployment created.';
      if (downloadedBootstrap && packageUrl) {
        gatewayFeedback = 'Gateway deployment created. Downloading bootstrap bundle and deployment package.';
      } else if (downloadedBootstrap) {
        gatewayFeedback = 'Gateway deployment created. Downloading bootstrap bundle.';
      } else if (packageUrl) {
        gatewayFeedback = 'Gateway deployment created. Downloading deployment package.';
      }

      setGatewayForm(emptyGatewayForm);
      setFeedback(gatewayFeedback);
      await loadPartner();
    } catch (error) {
      setErrorMessage(await apiErrorMessage(error, 'Unable to create gateway.'));
    } finally {
      setCreatingGateway(false);
    }
  }

  function parseJsonField(value, fieldLabel) {
    if (!value || !value.trim()) {
      return {};
    }
    try {
      return JSON.parse(value);
    } catch (error) {
      throw new Error(`${fieldLabel} must be valid JSON.`);
    }
  }

  async function handleCreateCapability(event) {
    event.preventDefault();
    setFeedback('');
    setErrorMessage('');
    setCreatingCapability(true);

    try {
      const properties = parseJsonField(capabilityForm.properties, 'Capability properties');
      const response = await axiosInstance.post(`/admin/partners/${partnerId}/business-units/${capabilityForm.business_unit_id}/capabilities`, {
        category_id: capabilityForm.category_id,
        source_capability_definition_id: capabilityForm.source_capability_definition_id,
        capability_name: capabilityForm.capability_name,
        global_response: capabilityForm.global_response === 'true',
        properties,
      });
      const createdCapability = response.data;
      setCapabilityForm((current) => ({
        ...emptyCapabilityForm,
        business_unit_id: createdCapability.business_unit_id || current.business_unit_id,
        category_id: current.category_id,
      }));
      setFeedback(`Created capability ${createdCapability.capability_name}.`);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error.message || 'Unable to create capability.');
    } finally {
      setCreatingCapability(false);
    }
  }

  async function handleCreateAsset(event) {
    event.preventDefault();
    setFeedback('');
    setErrorMessage('');
    setCreatingAsset(true);

    try {
      const properties = parseJsonField(assetForm.properties, 'Asset properties');
      const locationMetadata = parseJsonField(assetForm.location_metadata, 'Location metadata');
      const payload = {
        source_capability_asset_id: assetForm.source_capability_asset_id,
        asset_name: assetForm.asset_name,
        asset_type: assetForm.asset_type,
        asset_relationship_type: assetForm.asset_relationship_type,
        parent_asset_id: assetForm.parent_asset_id || null,
        facility_id: assetForm.facility_id || null,
        location_label: assetForm.location_label || null,
        location_metadata: locationMetadata,
        global_response: assetForm.global_response === 'true',
        latitude: assetForm.latitude === '' ? null : Number(assetForm.latitude),
        longitude: assetForm.longitude === '' ? null : Number(assetForm.longitude),
        properties,
      };
      await axiosInstance.post(`/admin/partners/${partnerId}/business-units/${assetForm.business_unit_id}/assets`, payload);
      setAssetForm((current) => ({
        ...emptyAssetForm,
        business_unit_id: current.business_unit_id,
        asset_type: current.asset_type,
        asset_relationship_type: current.asset_relationship_type,
      }));
      setFeedback('Created business unit asset.');
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error.message || 'Unable to create asset.');
    } finally {
      setCreatingAsset(false);
    }
  }

  async function handleSaveNeedCategories(event) {
    event.preventDefault();
    setFeedback('');
    setErrorMessage('');
    setSavingNeedCategories(true);

    try {
      await axiosInstance.put(`/admin/partners/${partnerId}/need-categories`, {
        need_categories: partnerNeedCategoryIds,
      });
      setFeedback('Partner need categories updated.');
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to update partner need categories.');
    } finally {
      setSavingNeedCategories(false);
    }
  }

  function handleAddNeedCategory() {
    if (!needCategoryDraft?.category_id) {
      return;
    }
    setPartnerNeedCategoryIds((current) => (
      current.includes(needCategoryDraft.category_id)
        ? current
        : [...current, needCategoryDraft.category_id]
    ));
    setCapabilityForm((current) => ({
      ...current,
      category_id: current.category_id || needCategoryDraft.category_id,
    }));
    setNeedCategoryDraft(null);
  }

  async function handleRemoveNeedCategory(categoryId) {
    const isSavedAttachment = Boolean(partner?.need_categories?.includes(categoryId));
    if (!isSavedAttachment) {
      setPartnerNeedCategoryIds((current) => current.filter((item) => item !== categoryId));
      setCapabilityForm((current) => ({
        ...current,
        category_id: current.category_id === categoryId ? '' : current.category_id,
      }));
      return;
    }

    setErrorMessage('');
    setFeedback('');
    setRemovingNeedCategoryId(categoryId);
    try {
      await axiosInstance.delete(`/admin/partners/${partnerId}/need-categories/${categoryId}`);
      setPartnerNeedCategoryIds((current) => current.filter((item) => item !== categoryId));
      setState((current) => {
        const partnerData = current.data?.partner;
        if (!partnerData) {
          return current;
        }
        return {
          ...current,
          data: {
            ...current.data,
            partner: {
              ...partnerData,
              need_categories: (partnerData.need_categories || []).filter((item) => item !== categoryId),
              attached_need_categories: (partnerData.attached_need_categories || []).filter((item) => item.category_id !== categoryId),
            },
          },
        };
      });
      setCapabilityForm((current) => ({
        ...current,
        category_id: current.category_id === categoryId ? '' : current.category_id,
      }));
      setFeedback('Need category removed from partner.');
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to remove need category from partner.');
    } finally {
      setRemovingNeedCategoryId('');
    }
  }

  function openCapabilityEditor(capability) {
    setEditingCapability(capability);
    setCapabilityEditForm(capabilityToForm(capability));
  }

  function closeCapabilityEditor() {
    if (savingCapabilityEdit) {
      return;
    }
    setEditingCapability(null);
    setCapabilityEditForm(emptyCapabilityForm);
  }

  async function handleUpdateCapability() {
    if (!editingCapability) {
      return;
    }
    setSavingCapabilityEdit(true);
    setFeedback('');
    setErrorMessage('');
    try {
      const properties = parseJsonField(capabilityEditForm.properties, 'Capability properties');
      await axiosInstance.patch(
        `/admin/partners/${partnerId}/business-units/${editingCapability.business_unit_id}/capabilities/${editingCapability.capability_definition_id}`,
        {
          category_id: capabilityEditForm.category_id,
          source_capability_definition_id: capabilityEditForm.source_capability_definition_id,
          capability_name: capabilityEditForm.capability_name,
          global_response: capabilityEditForm.global_response === 'true',
          properties,
        },
      );
      setFeedback('Capability updated.');
      closeCapabilityEditor();
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error.message || 'Unable to update capability.');
    } finally {
      setSavingCapabilityEdit(false);
    }
  }

  function openCapabilityDeleteDialog(capability) {
    setDeletingCapability(capability);
    setFeedback('');
    setErrorMessage('');
  }

  function closeCapabilityDeleteDialog() {
    if (!deletingCapabilitySaving) {
      setDeletingCapability(null);
    }
  }

  async function confirmDeleteCapability() {
    if (!deletingCapability) {
      return;
    }
    setFeedback('');
    setErrorMessage('');
    setDeletingCapabilitySaving(true);
    try {
      await axiosInstance.delete(`/admin/partners/${partnerId}/business-units/${deletingCapability.business_unit_id}/capabilities/${deletingCapability.capability_definition_id}`);
      setFeedback('Capability deleted.');
      setDeletingCapability(null);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to delete capability.');
    } finally {
      setDeletingCapabilitySaving(false);
    }
  }

  function openAssetEditor(asset) {
    setEditingAsset(asset);
    setAssetEditForm(assetToForm(asset));
  }

  function closeAssetEditor() {
    if (savingAssetEdit) {
      return;
    }
    setEditingAsset(null);
    setAssetEditForm(emptyAssetForm);
  }

  async function handleUpdateAsset() {
    if (!editingAsset) {
      return;
    }
    setSavingAssetEdit(true);
    setFeedback('');
    setErrorMessage('');
    try {
      const properties = parseJsonField(assetEditForm.properties, 'Asset properties');
      const locationMetadata = parseJsonField(assetEditForm.location_metadata, 'Location metadata');
      await axiosInstance.patch(
        `/admin/partners/${partnerId}/business-units/${editingAsset.business_unit_id}/assets/${editingAsset.asset_id}`,
        {
          source_capability_asset_id: assetEditForm.source_capability_asset_id,
          asset_name: assetEditForm.asset_name,
          asset_type: assetEditForm.asset_type,
          asset_relationship_type: assetEditForm.asset_relationship_type,
          parent_asset_id: assetEditForm.parent_asset_id || null,
          facility_id: assetEditForm.facility_id || null,
          location_label: assetEditForm.location_label || null,
          location_metadata: locationMetadata,
          global_response: assetEditForm.global_response === 'true',
          latitude: assetEditForm.latitude === '' ? null : Number(assetEditForm.latitude),
          longitude: assetEditForm.longitude === '' ? null : Number(assetEditForm.longitude),
          properties,
        },
      );
      setFeedback('Asset updated.');
      closeAssetEditor();
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || error.message || 'Unable to update asset.');
    } finally {
      setSavingAssetEdit(false);
    }
  }

  function openAssociateAsset(capability) {
    setAssociatingCapabilityId(capability?.capability_definition_id || '');
    setAssociateAssetId('');
    setFeedback('');
    setErrorMessage('');
  }

  function closeAssociateAsset() {
    if (associatingAssetSaving) {
      return;
    }
    setAssociatingCapabilityId('');
    setAssociateAssetId('');
  }

  function getAssociationAssetOptions(capability) {
    const associatedAssetIds = new Set((capability?.assets || []).map((asset) => asset.asset_id));
    return assets.filter((asset) => (
      asset.business_unit_id === capability?.business_unit_id
      && !associatedAssetIds.has(asset.asset_id)
    ));
  }

  async function handleAssociateAsset(capability) {
    if (!capability || !associateAssetId) {
      setErrorMessage('Select an asset from the same business unit before associating it.');
      return;
    }

    setAssociatingAssetSaving(true);
    setFeedback('');
    setErrorMessage('');
    try {
      await axiosInstance.post(
        `/admin/partners/${partnerId}/business-units/${capability.business_unit_id}/capabilities/${capability.capability_definition_id}/assets`,
        { asset_id: associateAssetId },
      );
      setFeedback('Asset associated with capability.');
      setAssociatingCapabilityId('');
      setAssociateAssetId('');
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to associate asset.');
    } finally {
      setAssociatingAssetSaving(false);
    }
  }

  function openAssetDeleteDialog(capability, asset) {
    setDeletingAssetContext({ capability, asset });
    setFeedback('');
    setErrorMessage('');
  }

  function closeAssetDeleteDialog() {
    if (!deletingAssetSaving) {
      setDeletingAssetContext(null);
    }
  }

  async function confirmDeleteAsset() {
    const asset = deletingAssetContext?.asset;
    if (!asset) {
      return;
    }
    setFeedback('');
    setErrorMessage('');
    setDeletingAssetSaving(true);
    try {
      const capability = deletingAssetContext?.capability;
      if (capability) {
        await axiosInstance.delete(
          `/admin/partners/${partnerId}/business-units/${capability.business_unit_id}/capabilities/${capability.capability_definition_id}/assets/${asset.asset_id}`,
        );
        setFeedback('Asset association removed.');
      } else {
        await axiosInstance.delete(`/admin/partners/${partnerId}/business-units/${asset.business_unit_id}/assets/${asset.asset_id}`);
        setFeedback('Asset deleted.');
      }
      setDeletingAssetContext(null);
      await loadPartner();
    } catch (error) {
      setErrorMessage(error?.response?.data?.message || 'Unable to delete asset.');
    } finally {
      setDeletingAssetSaving(false);
    }
  }

  const partner = state.data?.partner;
  const businessUnits = Array.isArray(state.data?.business_units) ? state.data.business_units : [];
  const userTargets = getUserTargetsFromState();
  const selectedUserTarget = getCurrentUserTarget();
  const resolvedSelectedUserTargetId = getTargetId(selectedUserTarget) || selectedUserTargetId || partnerId;
  const usersByTarget = state.data?.users_by_target && typeof state.data.users_by_target === 'object'
    ? state.data.users_by_target
    : {};
  const users = Array.isArray(usersByTarget[resolvedSelectedUserTargetId])
    ? usersByTarget[resolvedSelectedUserTargetId]
    : state.data?.users || [];
  const pendingInvitesByTarget = state.data?.pending_invites_by_target && typeof state.data.pending_invites_by_target === 'object'
    ? state.data.pending_invites_by_target
    : {};
  const pendingInvites = Array.isArray(pendingInvitesByTarget[resolvedSelectedUserTargetId])
    ? pendingInvitesByTarget[resolvedSelectedUserTargetId]
    : [];
  const allTargetUsers = userTargets.flatMap((target) => (
    Array.isArray(usersByTarget[getTargetId(target)]) ? usersByTarget[getTargetId(target)] : []
  ));
  const allTargetPendingInvites = userTargets.flatMap((target) => (
    Array.isArray(pendingInvitesByTarget[getTargetId(target)]) ? pendingInvitesByTarget[getTargetId(target)] : []
  ));
  const rootPartner = partner;
  const owningPartnerId = partner?.partner_id || '';
  const businessUnitOptions = getBusinessUnitOptionsFromData(state.data);
  const gateways = state.data?.gateways || [];
  const capabilities = state.data?.capabilities || [];
  const assets = state.data?.assets || [];
  const assetTypes = state.data?.asset_types || [];
  const activeAssetTypes = assetTypes.filter((assetType) => assetType.status === 'active');
  const assetCreateParentOptions = assets.filter((asset) => (
    !assetForm.business_unit_id || asset.business_unit_id === assetForm.business_unit_id
  ));
  const assetParentOptions = assets.filter((asset) => (
    asset.asset_id !== editingAsset?.asset_id
    && (!editingAsset?.business_unit_id || asset.business_unit_id === editingAsset.business_unit_id)
  ));
  const savedAttachedNeedCategories = Array.isArray(partner?.attached_need_categories)
    ? partner.attached_need_categories
    : [];
  const attachedNeedCategories = partnerNeedCategoryIds
    .map((categoryId) => (
      savedAttachedNeedCategories.find((item) => item.category_id === categoryId)
      || needCategories.find((item) => item.category_id === categoryId)
    ))
    .filter(Boolean);
  const availableNeedCategories = needCategories.filter(
    (category) => !partnerNeedCategoryIds.includes(category.category_id),
  );
  const capabilityCategoryOptions = attachedNeedCategories.length ? attachedNeedCategories : needCategories;
  const savedNeedCategoryIds = Array.isArray(partner?.need_categories) ? [...partner.need_categories].sort() : [];
  const currentNeedCategoryIds = [...partnerNeedCategoryIds].sort();
  const needCategoriesDirty = savedNeedCategoryIds.join('|') !== currentNeedCategoryIds.join('|');
	  const activeMoveSourcePartnerId = getUserSourcePartnerId(movingUser) || resolvedSelectedUserTargetId;
	  const moveTargetOptions = partnerOptions
    .filter((option) => option.partner_id !== activeMoveSourcePartnerId)
	    .sort((a, b) => getPartnerLabel(a).localeCompare(getPartnerLabel(b)));
	  const selectedMoveTarget = moveTargetOptions.find((option) => option.partner_id === moveForm.target_partner_id) || null;
	  const selectedInviteTarget = partnerOptions.find((option) => option.partner_id === inviteEditForm.target_partner_id) || null;
	  const movingSourcePartner = userTargets.find((target) => getTargetId(target) === activeMoveSourcePartnerId)
	    || partnerOptions.find((option) => option.partner_id === activeMoveSourcePartnerId)
	    || selectedUserTarget
	    || partner;
	  const removingSourcePartnerId = getUserSourcePartnerId(removingUser) || resolvedSelectedUserTargetId;
	  const removingSourcePartner = userTargets.find((target) => getTargetId(target) === removingSourcePartnerId)
	    || partnerOptions.find((option) => option.partner_id === removingSourcePartnerId)
	    || selectedUserTarget
	    || partner;
  const normalizedUserSearch = userSearch.trim().toLowerCase();
  const filteredUsers = users.filter((user) => {
    const matchesSearch = !normalizedUserSearch || [
      user.full_name,
      user.email,
      user.cognito_sub,
    ].some((value) => String(value || '').toLowerCase().includes(normalizedUserSearch));
    const matchesStatus = !userStatusFilter || user.status === userStatusFilter;
    const matchesRole = !userRoleFilter || user.role === userRoleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });
  const filteredPendingInvites = pendingInvites.filter((invite) => {
    const matchesSearch = !normalizedUserSearch || [
      invite.full_name,
      invite.email,
      invite.invite_id,
    ].some((value) => String(value || '').toLowerCase().includes(normalizedUserSearch));
    const matchesStatus = !userStatusFilter || ['invited', 'pending_login'].includes(userStatusFilter);
    const matchesRole = !userRoleFilter || invite.role === userRoleFilter;
    return matchesSearch && matchesStatus && matchesRole;
  });

  return (
    <AdminLayout
      props={props}
      breadcrumbItems={[
        { label: 'AMX xConsole', href: '/' },
        { label: 'Admin Dashboard', href: '/admin' },
        { label: 'Partner Registry', href: '/admin/partners' },
        { label: partner?.display_name || 'Partner Detail' },
      ]}
      title={partner?.display_name || 'Partner Detail'}
      subtitle="Update partner settings, manage users, and request gateway deployments."
    >
      {state.loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={24} />
          <Typography>Loading partner detail...</Typography>
        </Stack>
      ) : state.error ? (
        <Alert severity="error">{state.error}</Alert>
      ) : (
        <>
	          {feedback ? <Alert severity="success">{feedback}</Alert> : null}
	          {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

	          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
	            <Tabs
	              value={activeTab}
	              onChange={(_, value) => setActiveTab(value)}
	              variant="scrollable"
	              scrollButtons="auto"
	              allowScrollButtonsMobile
	            >
	              {partnerDetailTabs.map((tab) => (
	                <Tab key={tab.value} value={tab.value} label={tab.label} />
	              ))}
	            </Tabs>
	          </Box>

	          {activeTab === 'profile' ? (
	          <AdminSection title="Partner Profile" subtitle="Administrative settings and operational status for this partner.">
	            <Stack component="form" spacing={2} onSubmit={handlePartnerSave}>
	              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
	                <TextField label="Partner Code" value={partner?.partner_code || ''} disabled fullWidth />
	                <TextField label="Status" select value={partnerForm.status || 'active'} onChange={(event) => setPartnerForm({ ...partnerForm, status: event.target.value })} fullWidth>
	                  <MenuItem value="active">active</MenuItem>
	                  <MenuItem value="inactive">inactive</MenuItem>
	                  <MenuItem value="suspended">suspended</MenuItem>
	                </TextField>
	              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField label="Display Name" value={partnerForm.display_name || ''} onChange={(event) => setPartnerForm({ ...partnerForm, display_name: event.target.value })} fullWidth />
                <TextField label="Legal Name" value={partnerForm.legal_name || ''} onChange={(event) => setPartnerForm({ ...partnerForm, legal_name: event.target.value })} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField label="Contact Email" value={partnerForm.contact_email || ''} onChange={(event) => setPartnerForm({ ...partnerForm, contact_email: event.target.value })} fullWidth />
                <TextField label="Contact Phone" value={partnerForm.contact_phone || ''} onChange={(event) => setPartnerForm({ ...partnerForm, contact_phone: event.target.value })} fullWidth />
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(partnerForm.external_sharing_enabled)}
                      onChange={(event) => setPartnerForm({ ...partnerForm, external_sharing_enabled: event.target.checked })}
                    />
                  }
                  label="External Sharing Enabled"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(partnerForm.child_external_sharing_enabled)}
                      onChange={(event) => setPartnerForm({ ...partnerForm, child_external_sharing_enabled: event.target.checked })}
                    />
                  }
                  label="Business Unit External Sharing"
                />
              </Stack>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1}>
                  <Chip label={`${partner?.user_count || 0} users`} variant="outlined" />
                  <Chip label={`${partner?.gateway_count || 0} gateways`} variant="outlined" />
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button type="button" color="error" variant="outlined" startIcon={<DeleteForeverRoundedIcon />} onClick={openDeletePartnerDialog}>
                    Delete
                  </Button>
                  <Button type="submit" variant="contained">Save Partner</Button>
                </Stack>
	              </Stack>
	            </Stack>
	          </AdminSection>
	          ) : null}

	          {activeTab === 'business-units' ? (
	            <AdminSection
	              title="Business Units"
	              subtitle="Create, edit, activate, suspend, or permanently delete business units under this partner account."
	            >
	              <Stack spacing={3}>
	                <Stack component="form" spacing={2} onSubmit={handleSaveBusinessUnit}>
	                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
	                    <TextField
	                      label="Business Unit Code"
	                      value={businessUnitForm.business_unit_code}
	                      onChange={(event) => updateBusinessUnitForm('business_unit_code', event.target.value)}
	                      fullWidth
	                      required
	                    />
	                    <TextField
	                      label="Display Name"
	                      value={businessUnitForm.display_name}
	                      onChange={(event) => updateBusinessUnitForm('display_name', event.target.value)}
	                      fullWidth
	                      required
	                    />
	                    <TextField
	                      label="Legal Name"
	                      value={businessUnitForm.legal_name}
	                      onChange={(event) => updateBusinessUnitForm('legal_name', event.target.value)}
	                      fullWidth
	                    />
	                    <TextField
	                      label="Status"
	                      select
	                      value={businessUnitForm.status}
	                      onChange={(event) => updateBusinessUnitForm('status', event.target.value)}
	                      fullWidth
	                    >
	                      <MenuItem value="active">active</MenuItem>
	                      <MenuItem value="inactive">inactive</MenuItem>
	                      <MenuItem value="suspended">suspended</MenuItem>
	                    </TextField>
	                  </Stack>

	                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
	                    <TextField
	                      label="Contact Email"
	                      value={businessUnitForm.contact_email}
	                      onChange={(event) => updateBusinessUnitForm('contact_email', event.target.value)}
	                      fullWidth
	                    />
	                    <TextField
	                      label="Contact Phone"
	                      value={businessUnitForm.contact_phone}
	                      onChange={(event) => updateBusinessUnitForm('contact_phone', event.target.value)}
	                      fullWidth
	                    />
	                  </Stack>

	                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
	                    <FormControlLabel
	                      control={
	                        <Checkbox
	                          checked={Boolean(businessUnitForm.same_as_parent_address)}
	                          onChange={(event) => updateBusinessUnitForm('same_as_parent_address', event.target.checked)}
	                        />
	                      }
	                      label="Use parent partner address"
	                    />
	                    <FormControlLabel
	                      control={
	                        <Checkbox
	                          checked={Boolean(businessUnitForm.external_sharing_enabled)}
	                          onChange={(event) => updateBusinessUnitForm('external_sharing_enabled', event.target.checked)}
	                        />
	                      }
	                      label="External sharing enabled"
	                    />
	                  </Stack>

	                  {!businessUnitForm.same_as_parent_address ? (
	                    <Stack spacing={2}>
	                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
	                        <TextField
	                          label="Address Line 1"
	                          value={businessUnitForm.physical_address.line1}
	                          onChange={(event) => updateBusinessUnitAddress('line1', event.target.value)}
	                          fullWidth
	                        />
	                        <TextField
	                          label="Address Line 2"
	                          value={businessUnitForm.physical_address.line2}
	                          onChange={(event) => updateBusinessUnitAddress('line2', event.target.value)}
	                          fullWidth
	                        />
	                      </Stack>
	                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
	                        <TextField
	                          label="City"
	                          value={businessUnitForm.physical_address.city}
	                          onChange={(event) => updateBusinessUnitAddress('city', event.target.value)}
	                          fullWidth
	                        />
	                        <TextField
	                          label="State"
	                          value={businessUnitForm.physical_address.region}
	                          onChange={(event) => updateBusinessUnitAddress('region', event.target.value)}
	                          fullWidth
	                        />
	                        <TextField
	                          label="Zip Code"
	                          value={businessUnitForm.physical_address.postal_code}
	                          onChange={(event) => updateBusinessUnitAddress('postal_code', event.target.value)}
	                          fullWidth
	                        />
	                      </Stack>
	                    </Stack>
	                  ) : null}

	                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="flex-end">
	                    {editingBusinessUnitId ? (
	                      <Button type="button" variant="text" onClick={resetBusinessUnitForm} disabled={savingBusinessUnit}>
	                        Cancel Edit
	                      </Button>
	                    ) : null}
	                    <Button
	                      type="submit"
	                      variant="contained"
	                      startIcon={editingBusinessUnitId ? <EditRoundedIcon /> : <AddBusinessRoundedIcon />}
	                      disabled={savingBusinessUnit || !owningPartnerId}
	                    >
	                      {savingBusinessUnit
	                        ? 'Saving...'
	                        : editingBusinessUnitId
	                          ? 'Save Business Unit'
	                          : 'Create Business Unit'}
	                    </Button>
	                  </Stack>
	                </Stack>

	                <Box sx={{ overflowX: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
	                  <Table size="small" sx={{ minWidth: 960 }}>
	                    <TableHead>
	                      <TableRow>
	                        <TableCell>Business Unit</TableCell>
	                        <TableCell>Status</TableCell>
	                        <TableCell align="right">Users</TableCell>
	                        <TableCell align="right">Assets</TableCell>
	                        <TableCell>Sharing</TableCell>
	                        <TableCell>Contact</TableCell>
	                        <TableCell>Address</TableCell>
	                        <TableCell align="right">Actions</TableCell>
	                      </TableRow>
	                    </TableHead>
	                    <TableBody>
	                      {businessUnits.length ? businessUnits.map((unit) => (
	                        <TableRow key={unit.business_unit_id} hover>
	                          <TableCell sx={{ minWidth: 240 }}>
	                            <Typography variant="body2" sx={{ fontWeight: 700 }}>{getPartnerLabel(unit)}</Typography>
	                            <Typography variant="caption" color="text.secondary">{unit.business_unit_code}</Typography>
	                          </TableCell>
	                          <TableCell>
	                            <Chip size="small" label={unit.status || 'unknown'} color={unit.status === 'active' ? 'success' : unit.status === 'suspended' ? 'warning' : 'default'} variant="outlined" />
	                          </TableCell>
	                          <TableCell align="right">{unit.user_count || 0}</TableCell>
	                          <TableCell align="right">{unit.asset_count || 0}</TableCell>
	                          <TableCell>{unit.external_sharing_enabled ? 'Enabled' : 'Disabled'}</TableCell>
	                          <TableCell sx={{ minWidth: 220 }}>
	                            <Typography variant="body2">{unit.contact_email || '-'}</Typography>
	                            <Typography variant="caption" color="text.secondary">{unit.contact_phone || ''}</Typography>
	                          </TableCell>
	                          <TableCell sx={{ minWidth: 260 }}>
	                            {unit.physical_main_address || unit.physical_address?.formatted || '-'}
	                          </TableCell>
	                          <TableCell align="right">
	                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
	                              <Tooltip title="Edit business unit">
	                                <IconButton size="small" onClick={() => openBusinessUnitEditor(unit)}>
	                                  <EditRoundedIcon fontSize="small" />
	                                </IconButton>
	                              </Tooltip>
	                              <Tooltip title="Delete business unit">
	                                <IconButton size="small" color="error" onClick={() => openDeleteBusinessUnitDialog(unit)}>
	                                  <DeleteForeverRoundedIcon fontSize="small" />
	                                </IconButton>
	                              </Tooltip>
	                            </Stack>
	                          </TableCell>
	                        </TableRow>
	                      )) : (
	                        <TableRow>
	                          <TableCell colSpan={8}>
	                            <Typography variant="body2" color="text.secondary">
	                              No business units exist under this partner account.
	                            </Typography>
	                          </TableCell>
	                        </TableRow>
	                      )}
	                    </TableBody>
	                  </Table>
	                </Box>
	              </Stack>
	            </AdminSection>
	          ) : null}

	          {activeTab === 'need-categories' ? (
	          <AdminSection title="Need Category Attachments" subtitle="Attach supported need categories to this partner for partner discovery.">
            <Stack component="form" spacing={2} onSubmit={handleSaveNeedCategories}>
              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems={{ xs: 'stretch', lg: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Attached Categories
                      </Typography>
                      <Chip label={`${attachedNeedCategories.length} attached`} size="small" variant="outlined" />
                    </Stack>
                    {attachedNeedCategories.length ? (
                      <Stack sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                        {attachedNeedCategories.map((category) => (
                          <Box key={category.category_id} sx={{ py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Stack spacing={1}>
                              <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                                <div>
                                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                    {category.category_display_name || category.category_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {category.category_name}
                                  </Typography>
                                </div>
                                <Button
                                  size="small"
                                  variant="text"
                                  color="inherit"
                                  disabled={removingNeedCategoryId === category.category_id}
                                  onClick={() => handleRemoveNeedCategory(category.category_id)}
                                >
                                  {removingNeedCategoryId === category.category_id ? 'Removing...' : 'Remove'}
                                </Button>
                              </Stack>
                              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                <Chip size="small" label={category.scope} variant="outlined" />
                                <Chip size="small" label={category.status} variant="outlined" />
                                <Chip size="small" label={category.is_amx_owned ? 'AMX owned' : 'Partner owned'} variant="outlined" />
                              </Stack>
                              {category.description ? (
                                <Typography variant="body2" color="text.secondary">
                                  {category.description}
                                </Typography>
                              ) : null}
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        This partner does not have any supported need categories yet.
                      </Typography>
                    )}
                  </Stack>
                </Box>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Add Category
                      </Typography>
                      <Chip label={`${availableNeedCategories.length} available`} size="small" variant="outlined" />
                    </Stack>
                    <Autocomplete
                      options={availableNeedCategories}
                      value={needCategoryDraft}
                      onChange={(_, value) => setNeedCategoryDraft(value)}
                      getOptionLabel={(option) => option.category_display_name || option.category_name || ''}
                      isOptionEqualToValue={(option, value) => option.category_id === value.category_id}
                      renderOption={(props, option) => (
                        <li {...props} key={option.category_id}>
                          <ListItemText
                            primary={option.category_display_name || option.category_name}
                            secondary={`${option.category_name} - ${option.scope}`}
                          />
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Search Need Categories"
                          helperText="Choose a category to add it to this partner."
                        />
                      )}
                    />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                      <Typography variant="body2" color="text.secondary">
                        Added categories appear on the left immediately. Save to persist the attachment set.
                      </Typography>
                      <Button variant="outlined" onClick={handleAddNeedCategory} disabled={!needCategoryDraft}>
                        Add Category
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Stack>

              <Divider />

              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {partnerNeedCategoryIds.length ? (
                  partnerNeedCategoryIds.map((categoryId) => {
                    const category = attachedNeedCategories.find((item) => item.category_id === categoryId)
                      || needCategories.find((item) => item.category_id === categoryId);
                    return (
                      <Chip
                        key={categoryId}
                        label={category?.category_display_name || category?.category_name || categoryId}
                        variant="filled"
                        onDelete={() => handleRemoveNeedCategory(categoryId)}
                      />
                    );
                  })
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No categories selected for save yet.
                  </Typography>
                )}
              </Stack>

              <Stack direction="row" justifyContent="flex-end">
                <Button type="submit" variant="outlined" disabled={savingNeedCategories || !needCategoriesDirty}>
                  {savingNeedCategories ? 'Saving...' : 'Save Need Categories'}
                </Button>
              </Stack>
	            </Stack>
	          </AdminSection>
	          ) : null}

	          {activeTab === 'users' ? (
	          <AdminSection
	            title="AMX User Placement"
	            subtitle="AMX admins can invite, adjust, move, or remove users for this partner and its business-unit assignments."
            action={
              <Button variant="contained" startIcon={<PersonAddAltRoundedIcon />} onClick={openInviteDialog}>
                Invite User
              </Button>
            }
	          >
	            <Stack spacing={2}>
	              {userTargets.length > 1 ? (
	                <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
	                  <Tabs
	                    value={resolvedSelectedUserTargetId}
	                    onChange={(_, value) => setSelectedUserTargetId(value)}
	                    variant="scrollable"
	                    scrollButtons="auto"
	                    allowScrollButtonsMobile
	                  >
	                    {userTargets.map((target) => {
	                      const targetId = getTargetId(target);
	                      const targetUsers = usersByTarget[targetId] || [];
	                      const targetInvites = pendingInvitesByTarget[targetId] || [];
	                      return (
	                        <Tab
	                          key={targetId}
	                          value={targetId}
	                          icon={isBusinessUnit(target) ? <BusinessRoundedIcon fontSize="small" /> : undefined}
	                          iconPosition="start"
	                          label={`${getPartnerLabel(target)} (${targetUsers.length + targetInvites.length})`}
	                        />
	                      );
	                    })}
	                  </Tabs>
	                </Box>
	              ) : null}

	              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
	                <Chip label={`${users.length} target users`} variant="outlined" />
	                <Chip label={`${pendingInvites.length} pending invites`} color={pendingInvites.length ? 'info' : 'default'} variant="outlined" />
	                <Chip label={`${allTargetUsers.length || users.length} account users`} variant="outlined" />
	                <Chip label={`${allTargetPendingInvites.length || pendingInvites.length} account invites`} variant="outlined" />
	                <Chip label={isBusinessUnit(selectedUserTarget) ? 'Business unit scope' : 'Partner scope'} variant="outlined" />
	                <Chip label={`${users.filter((user) => user.role === 'partner_admin').length} admins`} variant="outlined" />
	                <Chip label={`${users.filter((user) => user.status === 'active').length} active`} variant="outlined" />
	                <Chip label={`${moveTargetOptions.length} move targets`} variant="outlined" />
              </Stack>

              <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', lg: 'center' }}>
                <TextField
                  label="Search Users"
                  value={userSearch}
                  onChange={(event) => setUserSearch(event.target.value)}
                  size="small"
                  sx={{ minWidth: { lg: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  select
                  label="Status"
                  value={userStatusFilter}
                  onChange={(event) => setUserStatusFilter(event.target.value)}
                  size="small"
                  sx={{ minWidth: { lg: 180 } }}
                >
                  <MenuItem value="">All statuses</MenuItem>
                  {userStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Role"
                  value={userRoleFilter}
                  onChange={(event) => setUserRoleFilter(event.target.value)}
                  size="small"
                  sx={{ minWidth: { lg: 160 } }}
                >
                  <MenuItem value="">All roles</MenuItem>
                  {userRoleOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                {partnerOptionsError ? (
                  <Alert severity="warning" sx={{ flex: 1 }}>{partnerOptionsError}</Alert>
                ) : null}
              </Stack>

              <Box sx={{ overflowX: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
                <Table size="small" sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Pending Invite</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Target</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPendingInvites.length ? filteredPendingInvites.map((invite) => (
                      <TableRow key={invite.invite_id} hover>
                        <TableCell sx={{ minWidth: 300 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                            {getInviteLabel(invite)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                            {invite.email}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.75} alignItems="flex-start">
                            <Chip size="small" label={invite.status || 'pending'} color={getInviteStatusColor(invite.status)} variant="outlined" />
                            <Typography variant="caption" color="text.secondary">
                              Expires {formatDate(invite.expires_at)}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {getPartnerLabel(invite)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {invite.business_unit_code || invite.partner_code || invite.target_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={getBusinessRoleLabel(invite.role)} variant="outlined" />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>{formatDate(invite.created_at)}</TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Resend invite">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => resendInvite(invite)}
                                  disabled={resendingInviteId === invite.invite_id}
                                >
                                  {resendingInviteId === invite.invite_id ? <CircularProgress size={18} /> : <RestartAltRoundedIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Move or edit invite">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => openInviteEditor(invite)}
                                  disabled={partnerOptionsLoading || !partnerOptions.length}
                                >
                                  <SwapHorizRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Revoke invite">
                              <IconButton size="small" color="error" onClick={() => openRevokeInviteDialog(invite)}>
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Typography variant="body2" color="text.secondary">
                            No pending invites match the current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>

              <Box sx={{ overflowX: 'auto', borderTop: '1px solid', borderColor: 'divider' }}>
                <Table size="small" sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Lifecycle</TableCell>
                      <TableCell>Membership</TableCell>
                      <TableCell>Activity</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredUsers.length ? filteredUsers.map((user) => (
                      <TableRow key={user.cognito_sub} hover>
                        <TableCell sx={{ minWidth: 300 }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar sx={{ width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700 }}>
                              {getUserInitials(user)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                                {getUserLabel(user)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-all' }}>
                                {user.email || user.cognito_sub}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack spacing={0.75} alignItems="flex-start">
                            <Chip size="small" label={user.status || 'unknown'} color={getStatusChipColor(user.status)} variant="outlined" />
                            {user.accepted_at ? (
                              <Typography variant="caption" color="text.secondary">Accepted {user.accepted_at}</Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            <Chip size="small" label={getBusinessRoleLabel(user.role)} variant="outlined" />
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ minWidth: 180 }}>
                          <Typography variant="body2">{user.last_activity_at || user.last_login_at || 'No activity yet'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Joined {user.joined_at || 'unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Manage user">
                              <IconButton size="small" onClick={() => openUserEditor(user)}>
                                <ManageAccountsRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Move user">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => openMoveUserDialog(user)}
                                  disabled={partnerOptionsLoading || !moveTargetOptions.length}
                                >
                                  <SwapHorizRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Remove membership only">
                              <IconButton size="small" color="error" onClick={() => openRemoveUserDialog(user)}>
                                <DeleteOutlineRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete AMX user">
                              <IconButton size="small" color="error" onClick={() => openDeleteUserDialog(user)}>
                                <DeleteForeverRoundedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            No users match the current filters.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
	            </Stack>
	          </AdminSection>
	          ) : null}

          <Dialog open={inviteOpen} onClose={closeInviteDialog} fullWidth maxWidth="sm">
            <DialogTitle>Invite User</DialogTitle>
            <Box component="form" onSubmit={handleAddUser}>
              <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <TextField
                    label="Email"
                    type="email"
                    value={userForm.email}
                    onChange={(event) => setUserForm({ ...userForm, email: event.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Full Name"
                    value={userForm.full_name}
                    onChange={(event) => setUserForm({ ...userForm, full_name: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Role"
                    select
                    value={userForm.role}
                    onChange={(event) => setUserForm({ ...userForm, role: event.target.value })}
                    fullWidth
                  >
                    {userRoleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button type="button" onClick={closeInviteDialog} disabled={inviteSaving}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={inviteSaving}>
                  {inviteSaving ? 'Inviting...' : 'Send Invite'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>

          <Dialog open={Boolean(editingInvite)} onClose={closeInviteEditor} fullWidth maxWidth="md">
            <DialogTitle>Manage Invite</DialogTitle>
            <Box component="form" onSubmit={handleSaveInviteEdit}>
              <DialogContent dividers>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                      {getInviteLabel(editingInvite)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {editingInvite?.email}
                    </Typography>
                  </Box>

                  <Autocomplete
                    options={partnerOptions}
                    loading={partnerOptionsLoading}
                    value={selectedInviteTarget}
                    onChange={(_, value) => setInviteEditForm({ ...inviteEditForm, target_partner_id: value?.partner_id || '' })}
                    getOptionLabel={getPartnerOptionLabel}
                    isOptionEqualToValue={(option, value) => option.partner_id === value.partner_id}
                    renderOption={(optionProps, option) => (
                      <li {...optionProps} key={option.partner_id}>
                        <ListItemText primary={getPartnerLabel(option)} secondary={getPartnerSecondary(option)} />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Target Partner"
                        helperText="Choose the partner where this invite should be accepted."
                        required
                      />
                    )}
                  />

                  <TextField
                    label="Full Name"
                    value={inviteEditForm.full_name}
                    onChange={(event) => setInviteEditForm({ ...inviteEditForm, full_name: event.target.value })}
                    fullWidth
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Role"
                      select
                      value={inviteEditForm.role}
                      onChange={(event) => setInviteEditForm({ ...inviteEditForm, role: event.target.value })}
                      fullWidth
                    >
                      {userRoleOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button type="button" onClick={closeInviteEditor} disabled={savingInviteEdit}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={savingInviteEdit || !inviteEditForm.target_partner_id}>
                  {savingInviteEdit ? 'Saving...' : 'Save Invite'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>

          <Dialog open={Boolean(revokingInvite)} onClose={closeRevokeInviteDialog} fullWidth maxWidth="sm">
            <DialogTitle>Revoke Invite</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                    {getInviteLabel(revokingInvite)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {revokingInvite?.email}
                  </Typography>
                </Box>
                <Alert severity="warning">
                  This removes the pending invite. The invite link will no longer be usable.
                </Alert>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeRevokeInviteDialog} disabled={savingInviteRevoke}>Cancel</Button>
              <Button color="error" variant="contained" onClick={revokeInvite} disabled={savingInviteRevoke}>
                {savingInviteRevoke ? 'Revoking...' : 'Revoke Invite'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(editingUser)} onClose={closeUserEditor} fullWidth maxWidth="sm">
            <DialogTitle>Manage User</DialogTitle>
            <Box component="form" onSubmit={handleSaveUserEdit}>
              <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 40, height: 40, fontSize: '0.9rem', fontWeight: 700 }}>
                      {getUserInitials(editingUser)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                        {getUserLabel(editingUser)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {editingUser?.email || editingUser?.cognito_sub}
                      </Typography>
                    </Box>
                  </Stack>
                  <TextField
                    label="Status"
                    select
                    value={userEditForm.status}
                    onChange={(event) => setUserEditForm({ ...userEditForm, status: event.target.value })}
                    fullWidth
                  >
                    {userStatusOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Role"
                    select
                    value={userEditForm.role}
                    onChange={(event) => setUserEditForm({ ...userEditForm, role: event.target.value })}
                    fullWidth
                  >
                    {userRoleOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                    ))}
                  </TextField>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button type="button" onClick={closeUserEditor} disabled={savingUserEdit}>Cancel</Button>
                <Button type="submit" variant="contained" disabled={savingUserEdit}>
                  {savingUserEdit ? 'Saving...' : 'Save User'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>

          <Dialog open={Boolean(movingUser)} onClose={closeMoveUserDialog} fullWidth maxWidth="md">
            <DialogTitle>Move User</DialogTitle>
            <Box component="form" onSubmit={handleMoveUser}>
              <DialogContent dividers>
                <Stack spacing={2.5} sx={{ pt: 1 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="overline" color="text.secondary">Current User</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                        {getUserLabel(movingUser)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {movingUser?.email || movingUser?.cognito_sub}
                      </Typography>
                    </Box>
	                    <Box sx={{ flex: 1, minWidth: 0 }}>
	                      <Typography variant="overline" color="text.secondary">Current Partner</Typography>
	                      <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
	                        {getPartnerLabel(movingSourcePartner)}
	                      </Typography>
	                      <Typography variant="body2" color="text.secondary">
	                        {getPartnerSecondary(movingSourcePartner)}
	                      </Typography>
	                    </Box>
                  </Stack>

                  <Autocomplete
                    options={moveTargetOptions}
                    loading={partnerOptionsLoading}
                    value={selectedMoveTarget}
                    onChange={(_, value) => setMoveForm({ ...moveForm, target_partner_id: value?.partner_id || '' })}
                    getOptionLabel={getPartnerOptionLabel}
                    isOptionEqualToValue={(option, value) => option.partner_id === value.partner_id}
                    renderOption={(props, option) => (
                      <li {...props} key={option.partner_id}>
                        <ListItemText
                          primary={getPartnerLabel(option)}
                          secondary={getPartnerSecondary(option)}
                        />
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Target Partner"
                        required
                        helperText="Choose where this user's partner membership should live."
                      />
                    )}
                  />

                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Target Role"
                      select
                      value={moveForm.role}
                      onChange={(event) => setMoveForm({ ...moveForm, role: event.target.value })}
                      fullWidth
                    >
                      {userRoleOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  {partnerOptionsError ? <Alert severity="warning">{partnerOptionsError}</Alert> : null}
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button type="button" onClick={closeMoveUserDialog} disabled={movingUserSaving}>Cancel</Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={movingUserSaving || !moveForm.target_partner_id}
                  startIcon={movingUserSaving ? <CircularProgress size={16} /> : <SwapHorizRoundedIcon />}
                >
                  {movingUserSaving ? 'Moving...' : 'Move User'}
                </Button>
              </DialogActions>
            </Box>
          </Dialog>

          <Dialog open={Boolean(removingUser)} onClose={closeRemoveUserDialog} fullWidth maxWidth="xs">
            <DialogTitle>Remove User</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {getUserLabel(removingUser)}
                </Typography>
	                <Typography variant="body2" color="text.secondary">
	                  Remove this user's membership from {getPartnerLabel(removingSourcePartner)}. Historical records remain intact.
	                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeRemoveUserDialog} disabled={removingUserSaving}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDeleteUser} disabled={removingUserSaving}>
                {removingUserSaving ? 'Removing...' : 'Remove User'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(deletingUser)} onClose={closeDeleteUserDialog} fullWidth maxWidth="sm">
            <DialogTitle>Delete AMX User</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                    {getUserLabel(deletingUser)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {deletingUser?.email || deletingUser?.cognito_sub}
                  </Typography>
                </Box>
                <Alert severity="error">
                  This permanently deletes the AMX user record, Cognito account, memberships, and AMX data created by this user.
                </Alert>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDeleteUserDialog} disabled={deletingUserSaving}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDeleteUserCompletely} disabled={deletingUserSaving}>
                {deletingUserSaving ? 'Deleting...' : 'Delete User'}
              </Button>
            </DialogActions>
          </Dialog>

	          <Dialog open={Boolean(deletingPartner)} onClose={closeDeletePartnerDialog} fullWidth maxWidth="sm">
	            <DialogTitle>Delete Partner Account</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                    {getPartnerLabel(partner)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                    {partner?.partner_code}
                  </Typography>
                </Box>
                <Alert severity="error">
                  This permanently deletes the partner account, its business units, and every AMX user under the account.
                </Alert>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDeletePartnerDialog} disabled={deletingPartnerSaving}>Cancel</Button>
              <Button color="error" variant="contained" onClick={handleDeletePartnerAccount} disabled={deletingPartnerSaving}>
                {deletingPartnerSaving ? 'Deleting...' : 'Delete'}
	              </Button>
	            </DialogActions>
	          </Dialog>

	          <Dialog open={Boolean(deletingBusinessUnit)} onClose={closeDeleteBusinessUnitDialog} fullWidth maxWidth="sm">
	            <DialogTitle>Delete Business Unit</DialogTitle>
	            <DialogContent dividers>
	              <Stack spacing={2} sx={{ pt: 1 }}>
	                <Box>
	                  <Typography variant="subtitle1" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
	                    {getPartnerLabel(deletingBusinessUnit)}
	                  </Typography>
	                  <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
	                    {deletingBusinessUnit?.business_unit_code}
	                  </Typography>
	                </Box>
	                <Alert severity="error">
	                  This permanently deletes the business unit and removes its user access grants. Partner user accounts remain. Deletion is blocked while AMX records still depend on this business unit; suspend it instead or remove those records first.
	                </Alert>
	              </Stack>
	            </DialogContent>
	            <DialogActions>
	              <Button onClick={closeDeleteBusinessUnitDialog} disabled={deletingBusinessUnitSaving}>Cancel</Button>
	              <Button color="error" variant="contained" onClick={handleDeleteBusinessUnit} disabled={deletingBusinessUnitSaving}>
	                {deletingBusinessUnitSaving ? 'Deleting...' : 'Delete Business Unit'}
	              </Button>
	            </DialogActions>
	          </Dialog>

	          {activeTab === 'gateways' ? (
	          <AdminSection title="Gateways" subtitle="Review partner and business-unit gateways, or request a new partner-owned gateway deployment.">
            <Stack component="form" spacing={2} onSubmit={handleCreateGateway}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField label="Gateway Code" value={gatewayForm.gateway_code} onChange={(event) => setGatewayForm({ ...gatewayForm, gateway_code: event.target.value })} fullWidth required />
                <TextField label="Display Name" value={gatewayForm.display_name} onChange={(event) => setGatewayForm({ ...gatewayForm, display_name: event.target.value })} fullWidth />
                <TextField label="Status" select value={gatewayForm.status} onChange={(event) => setGatewayForm({ ...gatewayForm, status: event.target.value })} fullWidth>
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="inactive">inactive</MenuItem>
                </TextField>
              </Stack>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="outlined"
                  disabled={creatingGateway}
                  startIcon={creatingGateway ? <CircularProgress size={16} /> : <DownloadRoundedIcon fontSize="small" />}
                >
                  {creatingGateway ? 'Creating Gateway' : 'Request Gateway Deployment'}
                </Button>
              </Stack>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Gateway</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>IoT Thing</TableCell>
                  <TableCell align="right">Topic Assignments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {gateways.map((gateway) => (
                  <TableRow key={gateway.gateway_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{gateway.display_name || gateway.gateway_code}</Typography>
                      <Typography variant="caption" color="text.secondary">{gateway.gateway_code}</Typography>
                    </TableCell>
                    <TableCell>{gateway.owner_type === 'business_unit' ? (gateway.business_unit_display_name || gateway.business_unit_code) : 'Partner account'}</TableCell>
                    <TableCell>{gateway.status}</TableCell>
                    <TableCell>{gateway.iot_thing_name || '-'}</TableCell>
                    <TableCell align="right">{gateway.topic_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
	            </Table>
	          </AdminSection>
	          ) : null}

	          {activeTab === 'capabilities' ? (
	          <AdminSection title="Capabilities And Assets" subtitle="Review current partner capabilities and assets, then add new records in the same workspace.">
            <Stack spacing={3}>
              <Box>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Current Capability Inventory
                    </Typography>
                    <Chip label={`${capabilities.length} capabilities`} variant="outlined" size="small" />
                  </Stack>

                  {capabilities.length ? (
                    <Stack sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                      {capabilities.map((capability) => {
                        const associationAssetOptions = getAssociationAssetOptions(capability);
                        const associationOpen = associatingCapabilityId === capability.capability_definition_id;
                        return (
                          <Box key={capability.capability_definition_id} sx={{ py: 2.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                            <Stack spacing={2}>
                              <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
                                <div>
                                  <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700 }}>
                                    {capability.capability_name}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Source ID: {capability.source_capability_definition_id}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Capability ID: {capability.capability_definition_id}
                                  </Typography>
                                </div>
                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap justifyContent={{ xs: 'flex-start', md: 'flex-end' }}>
                                  <Chip size="small" label={getBusinessUnitLabel(capability)} variant="outlined" />
                                  <Chip size="small" label={capability.category_display_name || capability.category_name || capability.category_id} variant="outlined" />
                                  <Chip size="small" label={capability.global_response ? 'Global response' : 'Partner scoped'} variant="outlined" />
                                  <Chip size="small" label={`${capability.assets?.length || 0} associated assets`} variant="outlined" />
                                </Stack>
                              </Stack>
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button size="small" variant="outlined" onClick={() => openCapabilityEditor(capability)}>
                                  Edit Capability
                                </Button>
                                <Button size="small" color="error" variant="outlined" onClick={() => openCapabilityDeleteDialog(capability)}>
                                  Delete Capability
                                </Button>
                              </Stack>

                              {capability.properties && Object.keys(capability.properties).length ? (
                                <TextField
                                  label="Capability Properties"
                                  value={JSON.stringify(capability.properties, null, 2)}
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  InputProps={{ readOnly: true }}
                                />
                              ) : null}

                              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                  Associated Assets
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => openAssociateAsset(capability)}
                                  disabled={!associationAssetOptions.length}
                                >
                                  Associate Asset
                                </Button>
                              </Stack>

                              {associationOpen ? (
                                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
                                  <TextField
                                    label="Asset"
                                    select
                                    value={associateAssetId}
                                    onChange={(event) => setAssociateAssetId(event.target.value)}
                                    fullWidth
                                    size="small"
                                  >
                                    {associationAssetOptions.map((asset) => (
                                      <MenuItem key={asset.asset_id} value={asset.asset_id}>
                                        {asset.asset_name || asset.source_capability_asset_id}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                                    <Button size="small" onClick={closeAssociateAsset} disabled={associatingAssetSaving}>
                                      Cancel
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => handleAssociateAsset(capability)}
                                      disabled={associatingAssetSaving || !associateAssetId}
                                    >
                                      {associatingAssetSaving ? 'Associating...' : 'Associate'}
                                    </Button>
                                  </Stack>
                                </Stack>
                              ) : null}

                              {capability.assets?.length ? (
                                <Stack sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                                  {capability.assets.map((asset) => (
                                    <Box key={asset.asset_id} sx={{ py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                                      <Stack spacing={1}>
                                        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1}>
                                          <div>
                                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                              {asset.asset_name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              Source ID: {asset.source_capability_asset_id}
                                            </Typography>
                                          </div>
                                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Chip size="small" label={asset.asset_type} variant="outlined" />
                                            <Chip size="small" label={asset.global_response ? 'Global response' : 'Partner scoped'} variant="outlined" />
                                          </Stack>
                                        </Stack>
                                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                                          <Button size="small" variant="outlined" onClick={() => openAssetEditor(asset)}>
                                            Edit Asset
                                          </Button>
                                          <Button size="small" color="error" variant="outlined" onClick={() => openAssetDeleteDialog(capability, asset)}>
                                            Remove Association
                                          </Button>
                                        </Stack>
                                      </Stack>
                                    </Box>
                                  ))}
                                </Stack>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  No assets are associated with this capability yet.
                                </Typography>
                              )}
                            </Stack>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      This partner has not published any capabilities yet.
                    </Typography>
                  )}

                  <Divider />

                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      Business Unit Assets
                    </Typography>
                    <Chip label={`${assets.length} assets`} variant="outlined" size="small" />
                  </Stack>
                  {assets.length ? (
                    <Stack sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
                      {assets.map((asset) => (
                        <Box key={asset.asset_id} sx={{ py: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={1.5}>
                            <div>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {asset.asset_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Source ID: {asset.source_capability_asset_id}
                              </Typography>
                            </div>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              <Chip size="small" label={getBusinessUnitLabel(asset)} variant="outlined" />
                              <Chip size="small" label={asset.asset_type_display_name || asset.asset_type} variant="outlined" />
                              <Chip size="small" label={asset.asset_relationship_type || 'standalone'} variant="outlined" />
                              <Chip size="small" label={asset.global_response ? 'Global response' : 'Partner scoped'} variant="outlined" />
                            </Stack>
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" variant="outlined" onClick={() => openAssetEditor(asset)}>
                                Edit Asset
                              </Button>
                              <Button size="small" color="error" variant="outlined" onClick={() => openAssetDeleteDialog(null, asset)}>
                                Delete Asset
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No business unit assets are registered yet.
                    </Typography>
                  )}
                </Stack>
              </Box>

              <Stack component="form" spacing={2} onSubmit={handleCreateCapability}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Add Capability
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Business Unit"
                    select
                    value={capabilityForm.business_unit_id}
                    onChange={(event) => setCapabilityForm({ ...capabilityForm, business_unit_id: event.target.value })}
                    fullWidth
                    required
                  >
                    {businessUnitOptions.map((unit) => (
                      <MenuItem key={getBusinessUnitId(unit)} value={getBusinessUnitId(unit)}>
                        {getBusinessUnitLabel(unit)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Need Category"
                    select
                    value={capabilityForm.category_id}
                  onChange={(event) => setCapabilityForm({ ...capabilityForm, category_id: event.target.value })}
                    fullWidth
                    required
                    helperText="Maps this capability to an attached or registered need category."
                  >
                    {capabilityCategoryOptions.map((category) => (
                      <MenuItem key={category.category_id} value={category.category_id}>
                        {category.category_display_name || category.category_name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Capability Source ID"
                    value={capabilityForm.source_capability_definition_id}
                    onChange={(event) => setCapabilityForm({ ...capabilityForm, source_capability_definition_id: event.target.value })}
                    fullWidth
                    required
                    helperText="Unique within this business unit."
                  />
                  <TextField
                    label="Capability Name"
                    value={capabilityForm.capability_name}
                    onChange={(event) => setCapabilityForm({ ...capabilityForm, capability_name: event.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Global Response"
                    select
                    value={capabilityForm.global_response}
                    onChange={(event) => setCapabilityForm({ ...capabilityForm, global_response: event.target.value })}
                    fullWidth
                  >
                    <MenuItem value="true">true</MenuItem>
                    <MenuItem value="false">false</MenuItem>
                  </TextField>
                </Stack>
                <TextField
                  label="Capability Properties JSON"
                  value={capabilityForm.properties}
                  onChange={(event) => setCapabilityForm({ ...capabilityForm, properties: event.target.value })}
                  fullWidth
                  multiline
                  minRows={4}
                />
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    New capabilities appear in the inventory above and can associate existing assets from the same business unit.
                  </Typography>
                  <Button type="submit" variant="contained" disabled={creatingCapability}>
                    {creatingCapability ? 'Creating...' : 'Create Capability'}
                  </Button>
                </Stack>
              </Stack>

              <Stack component="form" spacing={2} onSubmit={handleCreateAsset}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Add Asset
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Business Unit"
                    select
                    value={assetForm.business_unit_id}
                    onChange={(event) => setAssetForm({ ...assetForm, business_unit_id: event.target.value, parent_asset_id: '' })}
                    fullWidth
                    required
                  >
                    {businessUnitOptions.map((unit) => (
                      <MenuItem key={getBusinessUnitId(unit)} value={getBusinessUnitId(unit)}>
                        {getBusinessUnitLabel(unit)}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Relationship"
                    select
                    value={assetForm.asset_relationship_type}
                    onChange={(event) => setAssetForm({ ...assetForm, asset_relationship_type: event.target.value })}
                    fullWidth
                  >
                    {assetRelationshipOptions.map((relationshipType) => (
                      <MenuItem key={relationshipType} value={relationshipType}>
                        {relationshipType}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Asset Type"
                    select
                    value={assetForm.asset_type}
                    onChange={(event) => setAssetForm({ ...assetForm, asset_type: event.target.value })}
                    fullWidth
                    required
                  >
                    {activeAssetTypes.map((assetType) => (
                      <MenuItem key={assetType.asset_type_code} value={assetType.asset_type_code}>
                        {assetType.display_name || assetType.asset_type_code}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Asset Source ID"
                    value={assetForm.source_capability_asset_id}
                    onChange={(event) => setAssetForm({ ...assetForm, source_capability_asset_id: event.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Asset Name"
                    value={assetForm.asset_name}
                    onChange={(event) => setAssetForm({ ...assetForm, asset_name: event.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Parent Asset"
                    select
                    value={assetForm.parent_asset_id}
                    onChange={(event) => setAssetForm({ ...assetForm, parent_asset_id: event.target.value })}
                    fullWidth
                  >
                    <MenuItem value="">No parent asset</MenuItem>
                    {assetCreateParentOptions.map((asset) => (
                      <MenuItem key={asset.asset_id} value={asset.asset_id}>
                        {asset.asset_name || asset.source_capability_asset_id}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Facility ID"
                    value={assetForm.facility_id}
                    onChange={(event) => setAssetForm({ ...assetForm, facility_id: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Location Label"
                    value={assetForm.location_label}
                    onChange={(event) => setAssetForm({ ...assetForm, location_label: event.target.value })}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Latitude"
                    value={assetForm.latitude}
                    onChange={(event) => setAssetForm({ ...assetForm, latitude: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Longitude"
                    value={assetForm.longitude}
                    onChange={(event) => setAssetForm({ ...assetForm, longitude: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Global Response"
                    select
                    value={assetForm.global_response}
                    onChange={(event) => setAssetForm({ ...assetForm, global_response: event.target.value })}
                    fullWidth
                  >
                    <MenuItem value="true">true</MenuItem>
                    <MenuItem value="false">false</MenuItem>
                  </TextField>
                </Stack>
                <TextField
                  label="Location Metadata JSON"
                  value={assetForm.location_metadata}
                  onChange={(event) => setAssetForm({ ...assetForm, location_metadata: event.target.value })}
                  fullWidth
                  multiline
                  minRows={3}
                />
                <TextField
                  label="Asset Properties JSON"
                  value={assetForm.properties}
                  onChange={(event) => setAssetForm({ ...assetForm, properties: event.target.value })}
                  fullWidth
                  multiline
                  minRows={4}
                />
                <Stack direction="row" justifyContent="flex-end">
                  <Button type="submit" variant="outlined" disabled={creatingAsset}>
                    {creatingAsset ? 'Creating...' : 'Create Asset'}
                  </Button>
                </Stack>
              </Stack>
	            </Stack>
	          </AdminSection>
	          ) : null}

          <Dialog open={Boolean(editingCapability)} onClose={closeCapabilityEditor} fullWidth maxWidth="md">
            <DialogTitle>Edit Capability</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <TextField
                  label="Need Category"
                  select
                  value={capabilityEditForm.category_id}
                  onChange={(event) => setCapabilityEditForm({ ...capabilityEditForm, category_id: event.target.value })}
                  fullWidth
                  required
                >
                  {capabilityCategoryOptions.map((category) => (
                    <MenuItem key={category.category_id} value={category.category_id}>
                      {category.category_display_name || category.category_name}
                    </MenuItem>
                  ))}
                </TextField>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Capability Source ID"
                    value={capabilityEditForm.source_capability_definition_id}
                    onChange={(event) => setCapabilityEditForm({ ...capabilityEditForm, source_capability_definition_id: event.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Capability Name"
                    value={capabilityEditForm.capability_name}
                    onChange={(event) => setCapabilityEditForm({ ...capabilityEditForm, capability_name: event.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Global Response"
                    select
                    value={capabilityEditForm.global_response}
                    onChange={(event) => setCapabilityEditForm({ ...capabilityEditForm, global_response: event.target.value })}
                    fullWidth
                  >
                    <MenuItem value="true">true</MenuItem>
                    <MenuItem value="false">false</MenuItem>
                  </TextField>
                </Stack>
                <TextField
                  label="Capability Properties JSON"
                  value={capabilityEditForm.properties}
                  onChange={(event) => setCapabilityEditForm({ ...capabilityEditForm, properties: event.target.value })}
                  fullWidth
                  multiline
                  minRows={5}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCapabilityEditor} disabled={savingCapabilityEdit}>Cancel</Button>
              <Button onClick={handleUpdateCapability} variant="contained" disabled={savingCapabilityEdit}>
                {savingCapabilityEdit ? 'Saving...' : 'Save Capability'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(deletingCapability)} onClose={closeCapabilityDeleteDialog} fullWidth maxWidth="xs">
            <DialogTitle>Delete Capability</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {deletingCapability?.capability_name || 'Selected capability'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Delete this capability from {getBusinessUnitLabel(deletingCapability)}. Associated assets will remain in the business unit.
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeCapabilityDeleteDialog} disabled={deletingCapabilitySaving}>Cancel</Button>
              <Button color="error" variant="contained" onClick={confirmDeleteCapability} disabled={deletingCapabilitySaving}>
                {deletingCapabilitySaving ? 'Deleting...' : 'Delete Capability'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(editingAsset)} onClose={closeAssetEditor} fullWidth maxWidth="md">
            <DialogTitle>Edit Asset</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Relationship"
                    select
                    value={assetEditForm.asset_relationship_type}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, asset_relationship_type: event.target.value })}
                    fullWidth
                  >
                    {assetRelationshipOptions.map((relationshipType) => (
                      <MenuItem key={relationshipType} value={relationshipType}>
                        {relationshipType}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Asset Type"
                    select
                    value={assetEditForm.asset_type}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, asset_type: event.target.value })}
                    fullWidth
                    required
                  >
                    {activeAssetTypes.map((assetType) => (
                      <MenuItem key={assetType.asset_type_code} value={assetType.asset_type_code}>
                        {assetType.display_name || assetType.asset_type_code}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Asset Source ID"
                    value={assetEditForm.source_capability_asset_id}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, source_capability_asset_id: event.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Asset Name"
                    value={assetEditForm.asset_name}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, asset_name: event.target.value })}
                    fullWidth
                    required
                  />
                  <TextField
                    label="Parent Asset"
                    select
                    value={assetEditForm.parent_asset_id}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, parent_asset_id: event.target.value })}
                    fullWidth
                  >
                    <MenuItem value="">No parent asset</MenuItem>
                    {assetParentOptions.map((asset) => (
                      <MenuItem key={asset.asset_id} value={asset.asset_id}>
                        {asset.asset_name || asset.source_capability_asset_id}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Facility ID"
                    value={assetEditForm.facility_id}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, facility_id: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Location Label"
                    value={assetEditForm.location_label}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, location_label: event.target.value })}
                    fullWidth
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField
                    label="Latitude"
                    value={assetEditForm.latitude}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, latitude: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Longitude"
                    value={assetEditForm.longitude}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, longitude: event.target.value })}
                    fullWidth
                  />
                  <TextField
                    label="Global Response"
                    select
                    value={assetEditForm.global_response}
                    onChange={(event) => setAssetEditForm({ ...assetEditForm, global_response: event.target.value })}
                    fullWidth
                  >
                    <MenuItem value="true">true</MenuItem>
                    <MenuItem value="false">false</MenuItem>
                  </TextField>
                </Stack>
                <TextField
                  label="Location Metadata JSON"
                  value={assetEditForm.location_metadata}
                  onChange={(event) => setAssetEditForm({ ...assetEditForm, location_metadata: event.target.value })}
                  fullWidth
                  multiline
                  minRows={3}
                />
                <TextField
                  label="Asset Properties JSON"
                  value={assetEditForm.properties}
                  onChange={(event) => setAssetEditForm({ ...assetEditForm, properties: event.target.value })}
                  fullWidth
                  multiline
                  minRows={5}
                />
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeAssetEditor} disabled={savingAssetEdit}>Cancel</Button>
              <Button onClick={handleUpdateAsset} variant="contained" disabled={savingAssetEdit}>
                {savingAssetEdit ? 'Saving...' : 'Save Asset'}
              </Button>
            </DialogActions>
          </Dialog>

          <Dialog open={Boolean(deletingAssetContext)} onClose={closeAssetDeleteDialog} fullWidth maxWidth="xs">
            <DialogTitle>{deletingAssetContext?.capability ? 'Remove Asset Association' : 'Delete Asset'}</DialogTitle>
            <DialogContent dividers>
              <Stack spacing={1.5} sx={{ pt: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                  {deletingAssetContext?.asset?.asset_name || 'Selected asset'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {deletingAssetContext?.capability
                    ? `Remove this asset from ${deletingAssetContext.capability.capability_name}. The asset will remain in ${getBusinessUnitLabel(deletingAssetContext.asset)}.`
                    : `Delete this asset from ${getBusinessUnitLabel(deletingAssetContext?.asset)}.`}
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={closeAssetDeleteDialog} disabled={deletingAssetSaving}>Cancel</Button>
              <Button color="error" variant="contained" onClick={confirmDeleteAsset} disabled={deletingAssetSaving}>
                {deletingAssetSaving
                  ? (deletingAssetContext?.capability ? 'Removing...' : 'Deleting...')
                  : (deletingAssetContext?.capability ? 'Remove Association' : 'Delete Asset')}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </AdminLayout>
  );
}
